'use client'

import { useFormContext } from 'react-hook-form'
import { Checkbox as CheckboxUi } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import React, { useCallback, useState } from 'react'

import { Error } from '../Error'
import { Width } from '../Width'

type CheckboxGroupProps = {
  name: string
  label?: string
  options?: { label: string; value: string }[]
  required?: boolean
  width?: number
  errors: Record<string, unknown>
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  label,
  options,
  required,
  width,
  errors,
}) => {
  const { setValue, register } = useFormContext()
  const [selected, setSelected] = useState<string[]>([])

  // Register the field for validation
  register(name, {
    validate: (value) => {
      if (required && (!value || value.length === 0)) {
        return 'This field is required'
      }
      return true
    },
  })

  const handleChange = useCallback(
    (value: string, checked: boolean) => {
      setSelected((prev) => {
        const next = checked ? [...prev, value] : prev.filter((v) => v !== value)
        setValue(name, next.join(', '), { shouldValidate: true })
        return next
      })
    },
    [name, setValue],
  )

  return (
    <Width width={width}>
      {label && (
        <Label>
          {label}
          {required && (
            <span className="required">
              * <span className="sr-only">(required)</span>
            </span>
          )}
        </Label>
      )}
      {/* Figma 4097:110885 — checkboxes stack vertically, gap/gap-3 (12px). */}
      <div className="flex flex-col gap-3">
        {options?.map((option) => (
          <div key={option.value} className="flex items-center gap-3">
            <CheckboxUi
              id={`${name}-${option.value}`}
              checked={selected.includes(option.value)}
              onCheckedChange={(checked) => handleChange(option.value, Boolean(checked))}
            />
            <Label
              htmlFor={`${name}-${option.value}`}
              className="!text-[14px] !font-normal !leading-4 !normal-case cursor-pointer"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </div>
      {errors[name] ? <Error name={name} /> : null}
    </Width>
  )
}
